// src/components/common/DataTable.jsx
import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom"; // 🟢 IMPORT PORTAL
import T from "./T";
import api from "../../api/axiosConfig";
import { formatDate } from "../../utils/dateUtils";
import {
  ChevronUpDownIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ChevronDownIcon as ExpandDownIcon,
  ViewColumnsIcon,
  ArrowsUpDownIcon,
  CheckIcon,
  CalendarIcon,
  UserIcon
} from "@heroicons/react/24/outline";

const DataTable = ({
  data = [],
  columns = [],
  keyExtractor = (item) => item.id,
  emptyMessage = "No items found.",
  loading = false,
  loadingMessage = "Loading data...",
  renderExpandedRow = null,
  selectable = false,
  selectedIds = [],
  onSelectionChange = null,
  tableId = "default"
}) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [expandedRows, setExpandedRows] = useState({});
  
  // Auto-detect tableId if not passed or default
  const effectiveTableId = useMemo(() => {
    if (tableId && tableId !== "default") return tableId.toLowerCase().trim();
    if (typeof window !== "undefined" && window.location) {
      const parts = window.location.pathname.split("/").filter(Boolean);
      const last = parts[parts.length - 1] || "default";
      return last.toLowerCase().replace(/[^a-z0-9_-]/g, "");
    }
    return "default";
  }, [tableId]);

  // Column Visibility State - loaded and saved via backend API (/api/datatable)
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [loadingColumns, setLoadingColumns] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [apiSynced, setApiSynced] = useState(false);

  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);

  // 🟢 API: Fetch column preferences from backend using /api/datatable endpoint
  const fetchColumnPreferences = useCallback(async () => {
    if (!effectiveTableId) return;
    setLoadingColumns(true);
    setApiError(null);
    try {
      const colIds = columns.map(c => c.accessor || c.header).filter(Boolean).join(',');
      const res = await api.get('/datatable', {
        params: {
          table: effectiveTableId,
          cols: colIds
        }
      });
      if (res?.data?.success) {
        if (Array.isArray(res.data.hiddenColumns)) {
          setHiddenColumns(res.data.hiddenColumns);
        }
        setApiSynced(true);
        setApiError(null);
      } else {
        throw new Error(res?.data?.message || 'Failed to load column settings');
      }
    } catch (err) {
      const statusCode = err?.response?.status;
      const statusText = err?.response?.statusText;
      const errMsg = statusCode ? `API Error ${statusCode}${statusText ? ` (${statusText})` : ''}` : (err?.message || 'Failed to load preferences');
      console.warn(`[DataTable] Could not fetch column preferences for ${effectiveTableId}:`, errMsg);
      setApiError(errMsg);
      setApiSynced(false);
    } finally {
      setLoadingColumns(false);
    }
  }, [effectiveTableId, columns]);

  // Load preferences from API on mount and whenever effectiveTableId changes
  useEffect(() => {
    fetchColumnPreferences();
  }, [fetchColumnPreferences]);

  const [showColDropdown, setShowColDropdown] = useState(false);
  const [dropdownCoords, setDropdownCoords] = useState({ top: 0, right: 0 }); // 🟢 For Portal Positioning
  
  // 🟢 Custom Sort Dropdown State & Refs (fixes native select issues on mobile)
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [sortDropdownCoords, setSortDropdownCoords] = useState({ top: 0, left: 0 });
  const sortButtonRef = useRef(null);
  const sortDropdownRef = useRef(null);

  // 🟢 PORTAL LOGIC: Calculate exact position on screen when clicked & fetch from API
  const handleDropdownClick = (e) => {
    const trigger = e?.currentTarget || buttonRef.current;
    if (!showColDropdown && trigger) {
      const rect = trigger.getBoundingClientRect();
      setDropdownCoords({
        top: rect.bottom + 4, // 4px below the button
        right: Math.max(8, window.innerWidth - rect.right) // Align with right edge
      });
      // 🟢 API CALL: Load column preferences if not already synced
      if (!apiSynced) {
        fetchColumnPreferences();
      }
    }
    setShowColDropdown(prev => !prev);
  };

  const handleSortDropdownClick = (e) => {
    const trigger = e?.currentTarget || sortButtonRef.current;
    if (!showSortDropdown && trigger) {
      const rect = trigger.getBoundingClientRect();
      setSortDropdownCoords({
        top: rect.bottom + 4,
        left: Math.max(8, Math.min(rect.left, window.innerWidth - 232))
      });
    }
    setShowSortDropdown(prev => !prev);
  };

  // Close dropdowns on outside click or scroll
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Close if click is NOT on a columns trigger AND NOT in the dropdown
      if (
        dropdownRef.current && !dropdownRef.current.contains(event.target) &&
        !event.target.closest('[data-columns-trigger="true"]')
      ) {
        setShowColDropdown(false);
      }
      // Close if click is NOT on a sort trigger AND NOT in the sort dropdown
      if (
        sortDropdownRef.current && !sortDropdownRef.current.contains(event.target) &&
        !event.target.closest('[data-sort-trigger="true"]')
      ) {
        setShowSortDropdown(false);
      }
    };

    // 🟢 Close dropdown if the user scrolls the page (keeps portal from floating away)
    const handleScroll = () => {
      setShowColDropdown(false);
      setShowSortDropdown(false);
    };

    if (showColDropdown || showSortDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
      window.addEventListener("scroll", handleScroll, true); 
    }
    
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [showColDropdown, showSortDropdown]);

  // Filter active columns
  const activeColumns = useMemo(() => {
    return columns.filter(col => !hiddenColumns.includes(col.accessor || col.header));
  }, [columns, hiddenColumns]);

  // 🟢 API: Toggle column and persist updated visibility flags to server (/api/datatable)
  const toggleColumn = useCallback(async (colId) => {
    const nextHidden = hiddenColumns.includes(colId)
      ? hiddenColumns.filter((id) => id !== colId)
      : [...hiddenColumns, colId];

    // Optimistically update local state
    setHiddenColumns(nextHidden);

    // 🟢 Real API POST call to persist column flags (/api/datatable)
    try {
      await api.post('/api/datatable', {
        hiddenColumns: nextHidden,
        tableId: effectiveTableId
      });
      setApiSynced(true);
      setApiError(null);
    } catch (err) {
      console.warn(`[DataTable] Error saving column preferences for ${effectiveTableId}:`, err?.message);
      setApiError(err?.response?.status ? `Save failed (${err.response.status})` : 'Save failed');
    }
  }, [effectiveTableId, hiddenColumns]);

  // 🟢 API: Reset column preferences to default (/api/datatable/reset)
  const resetColumnsToDefault = useCallback(async () => {
    setHiddenColumns([]);
    try {
      await api.post('/api/datatable/reset', {
        tableId: effectiveTableId
      });
      setApiSynced(true);
      setApiError(null);
    } catch (err) {
      console.warn(`[DataTable] Error resetting column preferences for ${effectiveTableId}:`, err?.message);
      setApiError(err?.response?.status ? `Reset failed (${err.response.status})` : 'Reset failed');
    }
  }, [effectiveTableId]);

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") direction = "desc";
    setSortConfig({ key, direction });
  };

  const sortedData = useMemo(() => {
    let sortable = [...data];
    if (sortConfig.key) {
      sortable.sort((a, b) => {
        let aVal = a[sortConfig.key] ?? "";
        let bVal = b[sortConfig.key] ?? "";
        if (typeof aVal === "string") aVal = aVal.toLowerCase();
        if (typeof bVal === "string") bVal = bVal.toLowerCase();
        if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }
    return sortable;
  }, [data, sortConfig]);

  const handleSelectAll = (e) => onSelectionChange?.(e.target.checked ? sortedData.map(keyExtractor) : []);
  const handleSelectRow = (id) => onSelectionChange?.(selectedIds.includes(id) ? selectedIds.filter((i) => i !== id) : [...selectedIds, id]);
  const toggleExpand = (id) => setExpandedRows((prev) => ({ ...prev, [id]: !prev[id] }));

  const sortableColumns = activeColumns.filter((col) => col.sortable && col.accessor);

  return (
    <div className="flex flex-col w-full relative">
      
      {/* GLOBAL COLUMN CONFIGURATOR (Desktop) */}
      <div className="hidden md:flex justify-end mb-2 px-0">
        <div>
          <button 
            ref={buttonRef} // 🟢 ATTACH REF TO BUTTON
            data-columns-trigger="true"
            onClick={handleDropdownClick}
            className={`flex items-center gap-2 text-xs font-medium px-3 py-1.5 rounded-md shadow-2xs transition border ${showColDropdown ? 'bg-gray-100 dark:bg-slate-800 border-gray-400 dark:border-slate-600 text-gray-900 dark:text-white' : 'bg-white dark:bg-[#161b22] border-gray-300 dark:border-[#30363d] text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-800'}`}
          >
            <ViewColumnsIcon className="w-4 h-4" />
            <T>Columns</T>
          </button>
          
          {/* 🟢 THE MAGIC: createPortal teleports this menu to the <body> tag */}
          {showColDropdown && createPortal(
            <div 
              ref={dropdownRef}
              style={{
                position: 'fixed',
                top: `${dropdownCoords.top}px`,
                right: `${dropdownCoords.right}px`,
                zIndex: 99999 // Force it above EVERYTHING
              }}
              className="w-56 bg-white dark:bg-[#161b22] border border-gray-200 dark:border-[#30363d] shadow-2xl rounded-md p-2 max-h-[300px] overflow-y-auto animate-in fade-in zoom-in-95 duration-100"
            >
              <div className="text-[10px] font-bold text-gray-400 dark:text-slate-500 uppercase mb-2 px-1 border-b border-gray-100 dark:border-[#30363d] pb-2 flex items-center justify-between">
                <span><T>Visible Columns</T></span>
                {loadingColumns ? (
                  <span className="text-[10px] text-[#E8612C] font-normal flex items-center gap-1">
                    <span className="w-2 h-2 border border-[#E8612C] border-t-transparent rounded-full animate-spin inline-block" />
                    <span>Loading...</span>
                  </span>
                ) : apiSynced ? (
                  <span className="text-[9px] text-emerald-600 dark:text-emerald-400 font-normal">● Synced</span>
                ) : null}
              </div>
              {apiError && (
                <div className="mx-1 mb-2 p-1.5 rounded bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900/50 text-[10px] text-red-600 dark:text-red-400 flex items-center justify-between">
                  <span className="truncate">{apiError}</span>
                  <button 
                    type="button" 
                    onClick={() => fetchColumnPreferences()} 
                    className="underline hover:text-red-800 dark:hover:text-red-300 ml-1 shrink-0 font-medium cursor-pointer"
                  >
                    Retry
                  </button>
                </div>
              )}
              <div className="flex flex-col gap-0.5">
                {columns.map((col, idx) => {
                  const colId = col.accessor || col.header;
                  return (
                    <label key={idx} className="flex items-center gap-3 px-2 py-2 hover:bg-gray-50 dark:hover:bg-slate-800 rounded cursor-pointer text-xs text-gray-700 dark:text-slate-300 transition">
                      <input 
                        type="checkbox" 
                        checked={!hiddenColumns.includes(colId)}
                        onChange={() => toggleColumn(colId)}
                        className="rounded text-indigo-600 focus:ring-indigo-500 w-4 h-4 border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 cursor-pointer"
                      />
                      <span className="truncate font-medium">{col.header}</span>
                    </label>
                  );
                })}
              </div>
              
              {/* Footer controls: Reset to Default (active when columns are customized/hidden) */}
              {hiddenColumns.length > 0 && (
                <div className="mt-2 pt-2 border-t border-gray-100 dark:border-[#30363d] flex items-center justify-end px-1">
                  <button
                    type="button"
                    onClick={resetColumnsToDefault}
                    className="text-[11px] font-medium text-[#E8612C] hover:underline cursor-pointer transition-colors"
                  >
                    <T>Reset to Default</T>
                  </button>
                </div>
              )}
            </div>,
            document.body // 🟢 Teleport destination
          )}
        </div>
      </div>

      {/* ── MOBILE CARD VIEW (hidden on md+) ── */}
      <div className="md:hidden flex flex-col gap-2 p-0 bg-transparent min-h-0">
        {/* Compact Mobile Sort & Columns Row */}
        <div className="flex items-center justify-between gap-2 text-xs py-0.5 mb-1">
          {sortableColumns.length > 0 ? (
            <div className="flex items-center gap-1.5 flex-1 min-w-0">
              <button
                ref={sortButtonRef}
                data-sort-trigger="true"
                type="button"
                onClick={handleSortDropdownClick}
                className={`flex items-center gap-1.5 h-8 px-2.5 rounded-lg text-xs font-medium transition border truncate ${
                  showSortDropdown || sortConfig.key
                    ? 'bg-orange-50/70 dark:bg-orange-950/40 border-[#E8612C]/50 text-[#E8612C] shadow-2xs'
                    : 'bg-white dark:bg-[#161b22] border-gray-200 dark:border-[#30363d] text-slate-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-800'
                }`}
              >
                <ArrowsUpDownIcon className="w-3.5 h-3.5 shrink-0 text-slate-400 dark:text-slate-500" />
                <span className="truncate">
                  {sortConfig.key 
                    ? (sortableColumns.find(c => c.accessor === sortConfig.key)?.header || sortConfig.key)
                    : <T>Sort by</T>}
                </span>
                <ChevronDownIcon className={`w-3 h-3 shrink-0 text-slate-400 transition-transform ${showSortDropdown ? 'rotate-180 text-[#E8612C]' : ''}`} />
              </button>

              {sortConfig.key && (
                <button 
                  type="button"
                  onClick={() => setSortConfig((prev) => ({ ...prev, direction: prev.direction === "asc" ? "desc" : "asc" }))} 
                  title={sortConfig.direction === "asc" ? "Ascending (tap for Descending)" : "Descending (tap for Ascending)"}
                  className="flex items-center justify-center h-8 w-8 border border-[#E8612C]/50 rounded-lg text-xs bg-orange-50/70 dark:bg-orange-950/40 text-[#E8612C] shrink-0 hover:bg-orange-100/70 dark:hover:bg-orange-900/50 transition-colors shadow-2xs"
                >
                  {sortConfig.direction === "asc" ? <ChevronUpIcon className="w-3.5 h-3.5" strokeWidth={2.5} /> : <ChevronDownIcon className="w-3.5 h-3.5" strokeWidth={2.5} />}
                </button>
              )}
            </div>
          ) : <div className="flex-1" />}

          {/* Mobile Columns Button */}
          <div className="shrink-0">
            <button 
              data-columns-trigger="true"
              type="button"
              onClick={handleDropdownClick}
              className={`flex items-center gap-1.5 h-8 px-2.5 rounded-lg text-xs font-medium transition border ${
                showColDropdown 
                  ? 'bg-gray-100 dark:bg-slate-800 border-gray-400 dark:border-slate-600 text-gray-900 dark:text-white' 
                  : 'bg-white dark:bg-[#161b22] border-gray-200 dark:border-[#30363d] text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-800'
              }`}
            >
              <ViewColumnsIcon className="w-3.5 h-3.5" />
              <T>Columns</T>
            </button>
          </div>
        </div>

        {/* 🟢 CUSTOM SORT PORTAL DROPDOWN (teleported to body) */}
        {showSortDropdown && createPortal(
          <div 
            ref={sortDropdownRef}
            style={{
              position: 'fixed',
              top: `${sortDropdownCoords.top}px`,
              left: `${sortDropdownCoords.left}px`,
              zIndex: 99999
            }}
            className="w-56 bg-white dark:bg-[#161b22] border border-gray-200 dark:border-[#30363d] shadow-2xl rounded-xl p-1.5 max-h-[300px] overflow-y-auto animate-in fade-in zoom-in-95 duration-100"
          >
            <div className="text-[10px] font-bold text-gray-400 dark:text-slate-500 uppercase px-2 py-1.5 border-b border-gray-100 dark:border-[#30363d]/60 mb-1 flex items-center justify-between">
              <span><T>Sort by</T></span>
              {sortConfig.key && (
                <button
                  type="button"
                  onClick={() => {
                    setSortConfig({ key: null, direction: 'asc' });
                    setShowSortDropdown(false);
                  }}
                  className="text-[10px] font-medium text-red-500 dark:text-red-400 hover:underline cursor-pointer"
                >
                  <T>Clear</T>
                </button>
              )}
            </div>
            
            <div className="flex flex-col gap-0.5">
              <button
                type="button"
                onClick={() => {
                  setSortConfig({ key: null, direction: 'asc' });
                  setShowSortDropdown(false);
                }}
                className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-lg text-xs text-left transition cursor-pointer ${
                  !sortConfig.key
                    ? 'bg-orange-50 dark:bg-orange-950/40 text-[#E8612C] font-semibold'
                    : 'text-gray-700 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800'
                }`}
              >
                <span><T>— none —</T></span>
                {!sortConfig.key && <CheckIcon className="w-3.5 h-3.5 text-[#E8612C]" />}
              </button>

              {sortableColumns.map((col) => {
                const isSelected = sortConfig.key === col.accessor;
                return (
                  <button
                    key={col.accessor}
                    type="button"
                    onClick={() => {
                      setSortConfig((prev) => ({
                        key: col.accessor,
                        direction: isSelected ? (prev.direction === 'asc' ? 'desc' : 'asc') : 'asc'
                      }));
                      setShowSortDropdown(false);
                    }}
                    className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-lg text-xs text-left transition cursor-pointer ${
                      isSelected
                        ? 'bg-orange-50 dark:bg-orange-950/40 text-[#E8612C] font-semibold'
                        : 'text-gray-700 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800'
                    }`}
                  >
                    <span className="truncate">{col.header}</span>
                    {isSelected && (
                      <span className="flex items-center gap-1 shrink-0 text-[#E8612C]">
                        <span className="text-[10px] uppercase font-bold tracking-wider">
                          {sortConfig.direction === 'asc' ? 'ASC' : 'DESC'}
                        </span>
                        <CheckIcon className="w-3.5 h-3.5" />
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>,
          document.body
        )}

        {loading ? (
          <div className="flex flex-col items-center justify-center gap-2.5 py-12 border border-gray-200 dark:border-[#30363d] rounded-xl bg-white dark:bg-[#161b22]">
            <div className="w-7 h-7 border-2 border-[#E8612C] border-t-transparent rounded-full animate-spin" />
            <p className="text-xs font-semibold text-gray-500 dark:text-slate-400 animate-pulse"><T>{loadingMessage}</T></p>
          </div>
        ) : sortedData.length === 0 ? (
          <div className="text-center text-gray-500 dark:text-slate-400 text-xs py-8 border border-gray-200 dark:border-[#30363d] rounded-xl bg-white dark:bg-[#161b22]"><T>{emptyMessage}</T></div>
        ) : null}

        {sortedData.map((row) => {
          const rowKey = keyExtractor(row);
          const isExpanded = !!expandedRows[rowKey];
          const isSelected = selectedIds.includes(rowKey);

          // Identify semantic columns for clean mobile layout
          const titleCol = activeColumns[0];
          
          const statusCol = activeColumns.find(
            (c, i) => i > 0 && (c.accessor === 'status' || c.header?.toLowerCase().includes('status'))
          );
          
          const actionsCol = activeColumns.find(
            (c, i) => i > 0 && (c.header?.toLowerCase().includes('action') || c.accessor === 'actions' || (!c.accessor && c.align === 'right'))
          );
          
          const dateCol = activeColumns.find(
            (c, i) => i > 0 && c !== statusCol && c !== actionsCol && (
              c.accessor?.toLowerCase().includes('date') || 
              c.header?.toLowerCase().includes('date') ||
              c.accessor?.toLowerCase().includes('due') || 
              c.header?.toLowerCase().includes('due')
            )
          );
          
          const assignedCol = activeColumns.find(
            (c, i) => i > 0 && c !== statusCol && c !== actionsCol && c !== dateCol && (
              c.accessor?.toLowerCase().includes('employee') || 
              c.accessor?.toLowerCase().includes('assign') || 
              c.header?.toLowerCase().includes('assign') ||
              c.header?.toLowerCase().includes('employee')
            )
          );
          
          // Remaining columns to render in middle details
          const detailCols = activeColumns.filter(
            (c, i) => i > 0 && c !== statusCol && c !== actionsCol && c !== dateCol && c !== assignedCol
          );

          // Fallback row date detection with standard DD/MM/YYYY
          const rowDate = (row.startDate && row.endDate)
            ? `${formatDate(row.startDate)} — ${formatDate(row.endDate)}`
            : formatDate(row.startDate || row.dueDate || row.date || row.endDate || null);

          const dateValue = dateCol
            ? (dateCol.render ? dateCol.render(row, { toggleExpand, isExpanded }) : formatDate(row[dateCol.accessor]))
            : rowDate;

          return (
            <div 
              key={rowKey} 
              className={`bg-white dark:bg-[#161b22] border rounded-2xl p-3.5 shadow-2xs transition-all flex flex-col gap-2 ${
                isSelected 
                  ? "border-indigo-400 dark:border-indigo-500 ring-1 ring-indigo-300 dark:ring-indigo-700 bg-blue-50/20 dark:bg-blue-950/20" 
                  : "border-gray-200 dark:border-[#30363d]"
              }`}
            >
              {/* 1. Header: Clickable Card Title + Status Pill + Expand Chevron */}
              <div 
                onClick={() => toggleExpand(rowKey)}
                className="flex items-start justify-between gap-2.5 cursor-pointer select-none group"
              >
                <div className="flex items-start gap-2 flex-1 min-w-0">
                  {selectable && (
                    <div onClick={(e) => e.stopPropagation()}>
                      <input 
                        type="checkbox" 
                        checked={isSelected} 
                        onChange={() => handleSelectRow(rowKey)} 
                        className="mt-0.5 w-3.5 h-3.5 rounded border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-indigo-600 focus:ring-indigo-500 shrink-0 cursor-pointer" 
                      />
                    </div>
                  )}
                  <div className="font-bold text-xs md:text-sm text-gray-900 dark:text-white leading-snug break-words group-hover:text-[#E8612C] transition-colors">
                    {titleCol?.render ? titleCol.render(row, { toggleExpand, isExpanded }) : row[titleCol?.accessor] || "-"}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {statusCol && (
                    <div>
                      {statusCol.render ? statusCol.render(row, { toggleExpand, isExpanded }) : (
                        <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide border border-gray-200 dark:border-slate-700 text-gray-700 dark:text-slate-300">
                          {row[statusCol.accessor] || "-"}
                        </span>
                      )}
                    </div>
                  )}
                  <ChevronDownIcon className={`w-3.5 h-3.5 text-gray-400 dark:text-slate-500 transition-transform duration-200 ${isExpanded ? "rotate-180 text-[#E8612C]" : ""}`} />
                </div>
              </div>

              {/* 2. Expandable Middle Body: Details (Project, Assigned To, and other columns) */}
              {isExpanded && (detailCols.length > 0 || assignedCol || renderExpandedRow) && (
                <div className="flex flex-col gap-1.5 pt-1.5 pb-0.5 border-t border-dashed border-gray-100 dark:border-[#30363d] text-xs animate-in fade-in duration-150">
                  {detailCols.map((col, cIdx) => (
                    <div key={cIdx} className="flex items-start gap-1.5 leading-snug">
                      <span className="text-gray-500 dark:text-slate-400 shrink-0">{col.header}:</span>
                      <span className={`text-slate-800 dark:text-slate-200 ${col.cellClassName || ""}`}>
                        {col.render ? col.render(row, { toggleExpand, isExpanded }) : row[col.accessor] || "-"}
                      </span>
                    </div>
                  ))}

                  {assignedCol && (
                    <div className="flex items-center gap-1.5 leading-snug text-gray-500 dark:text-slate-400">
                      <UserIcon className="w-3.5 h-3.5 text-gray-400 dark:text-slate-500 shrink-0" />
                      <span className={`text-slate-800 dark:text-slate-200 ${assignedCol.cellClassName || ""}`}>
                        {assignedCol.render ? assignedCol.render(row, { toggleExpand, isExpanded }) : row[assignedCol.accessor] || "—"}
                      </span>
                    </div>
                  )}

                  {renderExpandedRow && (
                    <div className="mt-1 pt-1 border-t border-gray-100 dark:border-[#30363d]/60">
                      {renderExpandedRow(row)}
                    </div>
                  )}
                </div>
              )}

              {/* 3. Footer: Date/Hint on left + Action buttons on right (actions have stopPropagation) */}
              {(dateValue || actionsCol || detailCols.length > 0) && (
                <div className="flex items-center justify-between gap-2 pt-1 border-t border-gray-100/60 dark:border-[#30363d]/50">
                  <div 
                    onClick={() => toggleExpand(rowKey)}
                    className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-slate-400 min-w-0 truncate cursor-pointer select-none"
                  >
                    {dateValue ? (
                      <>
                        <CalendarIcon className="w-3.5 h-3.5 text-gray-400 dark:text-slate-500 shrink-0" />
                        <span className="truncate">{dateValue}</span>
                      </>
                    ) : (
                      <span className="text-[11px] text-gray-400 dark:text-slate-500 font-medium italic">
                        {isExpanded ? <T>Tap to collapse</T> : <T>Tap to view details</T>}
                      </span>
                    )}
                  </div>
                  {actionsCol && (
                    <div 
                      onClick={(e) => e.stopPropagation()} 
                      className="shrink-0 ml-auto"
                    >
                      {actionsCol.render ? actionsCol.render(row, { toggleExpand, isExpanded }) : null}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ── DESKTOP TABLE VIEW ── */}
      <div className="hidden md:block bg-white dark:bg-[#161b22] border border-slate-200/80 dark:border-[#30363d] overflow-x-auto rounded-2xl shadow-2xs transition-colors">
        <table className="w-full text-xs text-left text-slate-800 dark:text-slate-200 border-collapse whitespace-nowrap">
          <thead className="bg-slate-50/70 dark:bg-[#21262d] text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 border-b border-slate-200/80 dark:border-[#30363d] sticky top-0 z-10">
            <tr>
              {selectable && (
                <th className="px-4 py-3.5 w-10 text-center">
                  <input type="checkbox" checked={selectedIds?.length === sortedData.length && sortedData.length > 0} onChange={handleSelectAll} className="w-3.5 h-3.5 rounded border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-[#D9531E] focus:ring-[#D9531E] transition" />
                </th>
              )}
              {renderExpandedRow && <th className="px-3 py-3.5 w-8" />}
              
              {activeColumns.map((col, index) => (
                <th
                  key={index}
                  className={`px-4 py-3.5 transition-colors ${col.align === "right" ? "text-right" : col.align === "center" ? "text-center" : "text-left"} ${col.sortable ? "cursor-pointer hover:text-slate-900 dark:hover:text-white select-none group" : ""}`}
                  onClick={() => col.sortable && col.accessor ? requestSort(col.accessor) : null}
                >
                  <div className={`flex items-center gap-1.5 ${col.align === "right" ? "justify-end" : col.align === "center" ? "justify-center" : "justify-start"}`}>
                    <span>{col.header}</span>
                    {col.sortable && col.accessor && (
                      <span className="text-slate-400 dark:text-slate-500">
                        {sortConfig.key !== col.accessor ? <ChevronUpDownIcon className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity" /> : sortConfig.direction === "asc" ? <ChevronUpIcon className="w-3.5 h-3.5 text-[#D9531E]" /> : <ChevronDownIcon className="w-3.5 h-3.5 text-[#D9531E]" />}
                      </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>

          <tbody className="bg-white dark:bg-[#161b22] divide-y divide-slate-100 dark:divide-[#30363d]">
            {sortedData.length > 0 ? (
              sortedData.map((row) => {
                const rowKey = keyExtractor(row);
                const isExpanded = !!expandedRows[rowKey];
                const isSelected = selectedIds.includes(rowKey);

                return (
                  <React.Fragment key={rowKey}>
                    <tr className={`hover:bg-slate-50/80 dark:hover:bg-slate-800/60 transition-colors ${isSelected ? "bg-orange-50/30 dark:bg-orange-950/20" : ""}`}>
                      {selectable && (
                        <td className="px-3 py-3 text-center">
                          <input type="checkbox" checked={isSelected} onChange={() => handleSelectRow(rowKey)} className="w-3.5 h-3.5 rounded border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-[#D9531E] focus:ring-[#D9531E]" />
                        </td>
                      )}
                      {renderExpandedRow && (
                        <td className="px-2 py-3 text-center cursor-pointer" onClick={() => toggleExpand(rowKey)}>
                          {isExpanded ? <ExpandDownIcon className="w-3.5 h-3.5 text-slate-500 dark:text-slate-400 mx-auto" /> : <ChevronRightIcon className="w-3.5 h-3.5 text-slate-500 dark:text-slate-400 mx-auto" />}
                        </td>
                      )}
                      
                      {activeColumns.map((col, colIndex) => {
                        let cellContent = "-";
                        if (col.render) {
                          cellContent = col.render(row, { toggleExpand, isExpanded });
                        } else {
                          const val = row[col.accessor];
                          if (val !== undefined && val !== null && val !== "") {
                            if (typeof val === "string" && (/^\d{4}-\d{2}-\d{2}/.test(val) || col.accessor?.toLowerCase().includes("date") || col.header?.toLowerCase().includes("date"))) {
                              cellContent = formatDate(val);
                            } else {
                              cellContent = val;
                            }
                          }
                        }
                        return (
                          <td key={colIndex} className={`px-4 py-3 ${col.align === "right" ? "text-right" : col.align === "center" ? "text-center" : "text-left"} ${col.cellClassName || ""}`}>
                            {cellContent}
                          </td>
                        );
                      })}
                    </tr>

                    {renderExpandedRow && isExpanded && (
                      <tr className="bg-slate-50/50 dark:bg-slate-900/40">
                        <td colSpan={activeColumns.length + (selectable ? 1 : 0) + 1} className="px-4 py-3 border-t border-slate-100 dark:border-[#30363d]">
                          {renderExpandedRow(row)}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            ) : loading ? (
              <tr>
                <td colSpan={100} className="px-4 py-16 text-center">
                  <div className="flex flex-col items-center justify-center gap-2.5">
                    <div className="w-8 h-8 border-3 border-[#E8612C] border-t-transparent rounded-full animate-spin" />
                    <p className="text-xs font-semibold text-gray-500 dark:text-slate-400 animate-pulse tracking-wide"><T>{loadingMessage}</T></p>
                  </div>
                </td>
              </tr>
            ) : (
              <tr>
                <td colSpan={100} className="px-4 py-10 text-center text-slate-400 dark:text-slate-500 font-medium"><T>{emptyMessage}</T></td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DataTable;